import React from 'react';
import { DEFAULT_PAGE_SIZE, fetchReviewGroups } from '@services/fmdc';
import { QUEUE_SUBJECTS } from './fmdcUrl';

// The review queue's page of groups for one run, re-fetched whenever the URL-owned filters change.
// `reload()` re-runs the same query and resolves to the service result, so a caller that just
// decided rows can read the new `total` and clamp its offset.
//
// On a transient error the previous page is kept (the BackfillHistory rule) so a refresh that fails
// does not blank a queue a reviewer is working through.

// What the URL's filters send: `both` is the absence of a subject filter, and an empty reason or
// sheet is no filter at all.
export const queueRequestFrom = (runId, filters) => ({
  runId,
  subject: filters.subject === QUEUE_SUBJECTS.BOTH ? undefined : filters.subject,
  status: filters.status,
  reason: filters.reason || undefined,
  sheet: filters.sheet || undefined,
  limit: DEFAULT_PAGE_SIZE,
  offset: filters.offset || 0,
});

export const useReviewGroups = (runId, filters) => {
  const [groups, setGroups] = React.useState(null);
  const [total, setTotal] = React.useState(0);
  const [error, setError] = React.useState(null);

  const { subject, status, reason, sheet, offset } = filters;

  const load = React.useCallback(
    async (guard) => {
      const result = await fetchReviewGroups(queueRequestFrom(runId, { subject, status, reason, sheet, offset }));
      if (guard && guard.isCanceled) return result;
      if (result.error) {
        setError(result.error);
        setGroups((prev) => prev || []);
      } else {
        setError(null);
        setGroups(result.groups);
        setTotal(result.total);
      }
      return result;
    },
    [runId, subject, status, reason, sheet, offset]
  );

  React.useEffect(() => {
    const guard = { isCanceled: false };
    load(guard);
    return () => {
      guard.isCanceled = true;
    };
  }, [load]);

  const reload = React.useCallback(() => load(null), [load]);

  return {
    groups: groups || [],
    total,
    isLoading: groups === null,
    error,
    reload,
  };
};
